// RemoveAssetConfirmDialog — pre-action confirmation shown when a
// surveyor taps `−` on an asset row that's already at quantity 0.
//
// Replaces the post-action `RemovalUndoToast` so the destructive
// remove cannot fire until the surveyor has explicitly opted in
// (Confirm) or backed out (Cancel / Esc / overlay).
//
// Layered on the shared `<Dialog>` primitive: focus trap, Esc to
// close, return-focus-to-invoker, and sibling inert/aria-hidden
// isolation are all inherited. The shared dialog auto-focuses the
// first focusable descendant on open, so we render Confirm first in
// DOM order (and visually flip the row with `flex-row-reverse`) to
// make Confirm the auto-focus target — a fast user can tap-Enter
// without an extra hop, while keyboard tab order stays sensible.

import { type ReactElement } from 'react'

import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'

export interface RemoveAssetConfirmDialogProps {
  open: boolean
  assetName: string
  onCancel: () => void
  onConfirm: () => void
}

export function RemoveAssetConfirmDialog({
  open,
  assetName,
  onCancel,
  onConfirm,
}: RemoveAssetConfirmDialogProps): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={`Remove ${assetName}?`}
      description="This will remove it from this room."
    >
      <div
        data-testid="remove-asset-confirm-actions"
        className="flex flex-row-reverse flex-wrap justify-start gap-2"
      >
        <Button variant="danger" onClick={onConfirm}>
          Confirm
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Dialog>
  )
}
